import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
  signal,
} from '@angular/core';
import { AuthService, UserService } from '@app/shared/services';
import { select, Store } from '@ngrx/store';
import * as loginSelectors from '../../../root-store/login-store/login.selector';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { AboutMFONavService } from '@app/shared/services/about-mfo-nav/about-mfo-nav.service';
import { AboutMFOService } from '@app/shared/services/about-mfo/about-mfo.service';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { EventNames, EVENTS } from '@app/constants/events.constants';
import { getCurrentUserDetailsAll } from '../../../root-store/login-store/login.selector';
import { AboutMFOTitleEnum } from './about-mfo-enums';
import { ChatWidgetService } from '@app/shared/services/chat-widget/chat-widget.service';
import { UserTypeEnum } from '@app/enum/UserTypeEnum';

import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'about-mfo',
  templateUrl: './about-mfo.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AboutMFOComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() accountType;
  @ViewChild('sidenav') public sidenav: MatSidenav;
  @Output() closeAboutMFOSideBarEventEmitter = new EventEmitter();

  user$;
  showLoader = false;
  screenWidth: number;
  switchFromUser = false;
  subscription: Subscription = new Subscription();
  currentUser;

  steps: Array<{
    title: AboutMFOTitleEnum;
    completed: boolean;
    status: 'inactive' | 'active';
    subText?: string;
    listHeader?: string;
  }> = [];

  @Input() isImpersonating = false;
  resizeObservable$: Observable<Event>;
  currentActiveIndex = 0;
  currentActiveElement;
  markAsComplete = false;
  setupInstructions = [];
  videoSrc = 'https://www.loom.com/embed/dc0b88c881244b1396d9e85610544b3a?sid=21c0c15d-26a1-4963-ae47-ea5d3825e71e';

  constructor(
    private _userService: UserService,
    private store: Store,
    private cd: ChangeDetectorRef,
    private aboutMFONavService: AboutMFONavService,
    private aboutMFOService: AboutMFOService,
    private chatWidgetService: ChatWidgetService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.subscription.add(
      this.resizeObservable$.subscribe((evt) => {
        this.screenWidth = evt.target['innerWidth'];
      })
    );
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe(async (result) => {
        try {
          if (result) {
            this.isImpersonating = true;
          }
          const authUser = this.authService.user;
          if (result?.switch_from_user_id && result?.id != authUser['id']) {
            this.isImpersonating = true;
          } else {
            this.isImpersonating = false;
          }
          this.currentUser = result;
          const firstName = this.currentUser?.nick_name ?? this.currentUser?.first_name ?? this.currentUser?.first_middle_name;

          // Initialize steps for About MFO
          this.steps = [
            {
              title: AboutMFOTitleEnum.Introduction,
              completed: false,
              status: 'inactive',
              subText: 'Learn about your Family Office and its capabilities.',
              listHeader: 'Introduction to Kinnect',
            },
            {
              title: AboutMFOTitleEnum.Security,
              completed: false,
              status: 'inactive',
              subText: 'Understand the security measures and data protection policies.',
              listHeader: 'Security & Privacy',
            },
            {
              title: AboutMFOTitleEnum.TheBasics,
              completed: false,
              status: 'inactive',
              subText: 'Get familiar with the basic features and functionality.',
              listHeader: 'Getting Started',
            },
            {
              title: AboutMFOTitleEnum.Status,
              completed: false,
              status: 'inactive',
              subText: 'Complete your MFO setup process.',
              listHeader: 'Setup Status',
            },
          ];

          this.cd.detectChanges();
        } catch (ex) {}
      })
    );
    this.user$ = this.store.select(getCurrentUserDetailsAll);
    this.subscription.add(
      this.user$.subscribe((user) => {
        this.currentUser = user;
        this.cd.detectChanges();
      })
    );
    this.setupInstructions = [
      {
        image: 'assets/images/network.svg',
        title: 'Kinnect',
        videoSrc: 'https://www.loom.com/embed/d904b453bec74d0fb7fd15c7d7ba56f8?sid=21c0c15d-26a1-4963-ae47-ea5d3825e71e',
        description: 'We walk you through Kinnect’s four core phases that make family data management simple and secure.',
        note: 'Kinnect’s goal is to make it effortless to organize, understand, and share the details that matter most.',
        list: [
          `<span class="font-bold text-[#18191C]">Organization</span> – Gather and store all critical information in one protected place.`,
          `<span class="font-bold text-[#18191C]">Visualization</span> – Turn that data into clear charts and visuals that are easy to understand and share.`,
          `<span class="font-bold text-[#18191C]">Planning</span> – Decide who gets access to what, and when—whether it’s a spouse, child, or advisor.`,
          `<span class="font-bold text-[#18191C]">Communication</span> – Confidently invite others in once your data is complete, ensuring everyone sees exactly what they need.`,
        ],
      },
      {
        image: 'assets/images/security-key.svg',
        title: 'Security & Privacy',
        videoSrc: 'https://www.loom.com/embed/145027a9669d49299219e9ee76bc6d6d?sid=21c0c15d-26a1-4963-ae47-ea5d3825e71e',
        description: 'We explain how Kinnect protects your data with the highest security standards.',
        note: 'Your information stays private, protected, and fully under your control.',
        list: [
          '<span class="font-bold text-[#18191C]">Bank-grade encryption</span> keeps all your information fully protected—Kinnect can’t see your data on the back end.',
          '<span class="font-bold text-[#18191C]">Multi-factor authentication</span> ensures only verified users can access accounts.',
          '<span class="font-bold text-[#18191C]">Premium encryption keys</span> let you add extra protection to individual files.',
          '<span class="font-bold text-[#18191C]">SOC 2 Type II certification</span> and ongoing <span class="font-bold text-[#18191C]">vulnerability testing</span> confirm that Kinnect meets strict industry standards for security and reliability.',
        ],
      },
      {
        image: 'assets/images/mind.svg',
        title: 'The Basics',
        videoSrc: 'https://www.loom.com/embed/acf672dd3466454e9c68e641ea03fe83?sid=21c0c15d-26a1-4963-ae47-ea5d3825e71e',
        description: 'We outline how Kinnect structures your data and accounts for clarity and collaboration.',
        note: 'This system keeps your data structured, accurate, and easy to understand for everyone involved.',
        list: [
          '<span class="font-bold text-[#18191C]">Individual Accounts</span> – Every person gets their own account, though you can manage others’ (like a spouse or child) if needed.',
          '<span class="font-bold text-[#18191C]">Vaults Based on Ownership</span> – Each unique owner or ownership structure automatically creates its own vault—personal, shared, or entity-based.',
          '<span class="font-bold text-[#18191C]">Smart Organization</span> – Items always live in the vault of the thing that owns them, keeping data clean and logical.',
          '<span class="font-bold text-[#18191C]">Automated Reports</span> – As you add assets, accounts, and values, Kinnect automatically builds ownership maps, asset and liability reports, and income and expense visuals.',
        ],
      },
    ];
  }

  ngAfterViewInit() {
    this.steps[0].completed = false;
    this.setStatusForSidenavMenu(0);
    this.trackEventsInMixPanel([{ eventName: EVENTS.about_mfo_instruction_start }]);
  }

  closeSideNav() {
    let events = [{ eventName: EVENTS.about_mfo_close }];
    if (this.markAsComplete) events.push({ eventName: EVENTS.about_mfo_status_complete });
    this.trackEventsInMixPanel(events);
    this.aboutMFOService.updateSetupData([]);
    this.aboutMFONavService.handleNav(false);
    this.closeAboutMFOSideBarEventEmitter.emit(false);

    // Ensure status is set to 'inprogress' if user opened the side nav but didn't complete it
    if (this.currentUser && (!this.currentUser.intro_complete || this.currentUser.intro_complete === null)) {
      this.updateIntroStatus('inprogress');
      // Emit status update to notify other components
      this.aboutMFONavService.handleAboutMFOStatusUpdate('inprogress');
    }

    // Check intro_complete status when closing
    this.checkIntroCompleteStatus();
  }

  checkIntroCompleteStatus() {
    if (this.currentUser && this.currentUser.intro_complete) {
      const status = this.currentUser.intro_complete;

      switch (status) {
        case 'complete':
          // Handle completed state - maybe show success message or redirect
          // User has completed the About MFO setup
          break;
        case 'inprogress':
          // Handle in progress state - maybe show reminder or keep status as is
          // User started but didn't complete the About MFO setup
          break;
        case null:
        case undefined:
          // Handle not started state
          // User hasn't started the About MFO setup
          break;
        default:
          console.warn('Unknown About MFO status:', status);
          break;
      }
    } else {
      // About MFO status is null/undefined - user hasn't started
    }
  }

  ngOnDestroy(): void {
    this.markAsComplete = false;
    this.subscription.unsubscribe();
    this._userService.cleanupWorker();
  }

  handleSidenavMenuNavigation(index?: number) {
    if (!!index || index == 0) {
      this.setStatusForSidenavMenu(index);
    } else {
      this.setStatusForSidenavMenu();
    }
  }

  trackEventsInMixPanel(events: { eventName: EventNames; data?: any }[]) {
    this._userService.trackEventsInMixPanel(events).then((response) => {});
  }

  setStatusForSidenavMenu(activeIndex?: number) {
    if (activeIndex === undefined) {
      // Move to the next step
      if (this.currentActiveIndex !== undefined && this.currentActiveIndex < this.steps.length - 1) {
        activeIndex = this.currentActiveIndex + 1;
      } else {
        // If no more steps are available, do nothing or reset to the first step
        activeIndex = 0; // or handle this case based on your requirements
      }
    }

    if (activeIndex !== -1 && activeIndex < this.steps.length) {
      for (let i = 0; i < this.steps.length; i++) {
        if (i === activeIndex) {
          this.steps[i].status = 'active';
          this.currentActiveElement = this.steps[i];
        } else {
          this.steps[i].status = 'inactive';
        }
      }
      this.markAsComplete = false;
      this.currentActiveIndex = activeIndex;
      this.cd.detectChanges();
    }

    if (this.currentActiveIndex == 1) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.about_mfo_instruction_end }, { eventName: EVENTS.about_mfo_security_start }]);
    } else if (this.currentActiveIndex == 2) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.about_mfo_security_end }, { eventName: EVENTS.about_mfo_basics_start }]);
    } else if (this.currentActiveIndex == 3) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.about_mfo_basics_end }, { eventName: EVENTS.about_mfo_status_start }]);
    } else if (this.currentActiveIndex == 4) {
      this.trackEventsInMixPanel([{ eventName: EVENTS.about_mfo_close }]);
    }
  }

  submitAllFormAnswers(value) {
    this.markAsComplete = !!value;
    if (!!value) {
      this.completeSetup();
    }
  }

  gotoPrevious() {
    if (this.currentActiveIndex > 0) {
      const exist = this.aboutMFOService.getSetupData().find((item) => item.step == this.currentActiveIndex);

      this.currentActiveIndex = this.currentActiveIndex - 1;

      for (let i = 0; i < this.steps.length; i++) {
        if (i === this.currentActiveIndex) {
          this.steps[i].status = 'active';
          this.currentActiveElement = this.steps[i];
        } else {
          this.steps[i].status = 'inactive';
        }
      }
      this.cd.detectChanges();
    }
  }

  handleOptionValueEmitter(value) {
    if (value == 'next') this.handleSidenavMenuNavigation();
  }

  updateIntroStatus(status: string) {
    if (this.currentUser && this.currentUser.id) {
      this.subscription.add(
        this._userService.updateUser({ intro_complete: status }).subscribe({
          next: (response) => {
            // Update the current user object with the new status
            this.currentUser.intro_complete = status;
            this.cd.detectChanges();
          },
          error: (error) => {
            console.error('Error updating intro status:', error);
          },
        })
      );
    }
  }

  completeSetup() {
    // Update intro_complete to 'complete'
    this.updateIntroStatus('complete');

    // Emit status update to notify other components
    this.aboutMFONavService.handleAboutMFOStatusUpdate('complete');
  }

  handleSetupText(text: AboutMFOTitleEnum) {
    if (text == AboutMFOTitleEnum.Introduction) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >About
        <span class="text-k-blue font-bold"> My Family Office</span>
      </span>
  `;
    }
    if (text == AboutMFOTitleEnum.Security) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >
        <span class="text-k-blue font-bold">Security & Privacy</span>
      </span>
    `;
    }
    if (text == AboutMFOTitleEnum.TheBasics) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >
        <span class="text-k-blue font-bold">The Basics</span>
      </span>
    `;
    }
    if (text == AboutMFOTitleEnum.Status) {
      return `
      <span class="font-weight-bold-blue text-xl font-bold"
        >About MFO
        <span class="text-k-blue font-bold"> Status </span>
      </span>
  `;
    }
  }
}
